import { useMemo } from 'react'
import { Card, Tag, Title, Text, Icon } from '@nimbus-ds/components'
import {
  LightbulbIcon,
  ExclamationTriangleIcon,
  StatsIcon,
  ShoppingCartIcon,
  UserGroupIcon,
  FlagIcon,
  ArrowRightIcon,
} from '@nimbus-ds/icons'

interface Insight {
  type: 'warning' | 'info' | 'success' | 'opportunity'
  title: string
  description: string
  action?: string
}

interface ChartDataStep {
  name: string
  users: number
  usersEntered: number
  entryConversionRate: number
  progressionRate: number
  isConversion?: boolean
  stepNumber: number
  entry_sources?: {
    external?: Record<string, number>
    internal?: Record<string, number>
  }
}

interface OptimizationOpportunitiesProps {
  insights: Insight[]
  chartData: ChartDataStep[]
  /** Render without the outer Card wrapper so the section can be embedded into another card. */
  bare?: boolean
}

interface PrioritizedOpportunity {
  priority: 1 | 2 | 3 | 4 | 5
  type: 'product' | 'checkout' | 'traffic' | 'general'
  icon: React.ReactNode
  title: string
  description: string
  action?: string
  metric?: string
}

export function OptimizationOpportunities({
  insights,
  chartData,
  bare = false,
}: OptimizationOpportunitiesProps) {
  const opportunities = useMemo(() => {
    const ops: PrioritizedOpportunity[] = []

    // Process insights and add priority scoring
    insights.forEach(insight => {
      let priority: 1 | 2 | 3 | 4 | 5 = 5
      let type: 'product' | 'checkout' | 'traffic' | 'general' = 'general'
      let icon: React.ReactNode = <Icon source={<LightbulbIcon />} color="currentColor" />

      // Determine priority based on insight content
      if (insight.title.includes('convierte') && insight.title.includes('%')) {
        // Product underperformer
        if (insight.title.includes('visitas')) {
          priority = 1 // High-traffic product underperformer
          type = 'product'
          icon = <Icon source={<ShoppingCartIcon />} color="currentColor" />
        } else {
          priority = 4 // Moderate product issue
          type = 'product'
          icon = <Icon source={<ShoppingCartIcon />} color="currentColor" />
        }
      } else if (insight.title.includes('completa la compra') || insight.title.includes('checkout')) {
        priority = 2 // Checkout bottleneck
        type = 'checkout'
        icon = <Icon source={<FlagIcon />} color="currentColor" />
      } else if (insight.title.includes('Google') || insight.title.includes('Facebook') || insight.title.includes('Directo')) {
        priority = 3 // Traffic source issue
        type = 'traffic'
        icon = <Icon source={<UserGroupIcon />} color="currentColor" />
      } else if (insight.type === 'success') {
        priority = 5 // General health (lowest priority for opportunities)
        type = 'general'
        icon = <Icon source={<StatsIcon />} color="currentColor" />
      } else if (insight.type === 'warning') {
        priority = 2
        icon = <Icon source={<ExclamationTriangleIcon />} color="currentColor" />
      }

      ops.push({
        priority,
        type,
        icon,
        title: insight.title,
        description: insight.description,
        action: insight.action,
      })
    })

    // Add checkout-specific insight if not already present
    const conversionStep = chartData.find(s => s.isConversion)
    const preConversionStep = chartData.find(s => s.stepNumber === (conversionStep?.stepNumber || 0) - 1)

    // Require a meaningful sample before flagging a checkout bottleneck — a few
    // sessions is noise, not a funnel problem.
    if (preConversionStep && preConversionStep.progressionRate < 30 && preConversionStep.users >= 20) {
      const hasCheckoutInsight = ops.some(o => o.type === 'checkout')
      if (!hasCheckoutInsight) {
        ops.push({
          priority: 2,
          type: 'checkout',
          icon: <Icon source={<FlagIcon />} color="currentColor" />,
          title: `Cuello de botella en "${preConversionStep.name}"`,
          description: `Solo ${preConversionStep.progressionRate}% de los usuarios completan la compra desde este paso.`,
          action: 'Revisa costos de envio, metodos de pago y formularios del checkout.',
          metric: `${preConversionStep.progressionRate}% progresion`,
        })
      }
    }

    // Sort by priority (1 = highest)
    return ops.sort((a, b) => a.priority - b.priority)
  }, [insights, chartData])

  const getPriorityLabel = (priority: number): { label: string; appearance: 'danger' | 'warning' | 'neutral' } => {
    switch (priority) {
      case 1:
        return { label: 'Alta', appearance: 'danger' }
      case 2:
        return { label: 'Alta', appearance: 'danger' }
      case 3:
        return { label: 'Media', appearance: 'warning' }
      case 4:
        return { label: 'Media', appearance: 'warning' }
      default:
        return { label: 'Info', appearance: 'neutral' }
    }
  }

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'product':
        return 'Producto'
      case 'checkout':
        return 'Checkout'
      case 'traffic':
        return 'Trafico'
      default:
        return 'General'
    }
  }

  const getCardStyle = (priority: number) => {
    switch (priority) {
      case 1:
      case 2:
        return 'border-l-4 border-l-red-400 bg-red-50/50'
      case 3:
      case 4:
        return 'border-l-4 border-l-amber-400 bg-amber-50/50'
      default:
        return 'border-l-4 border-l-blue-400 bg-blue-50/50'
    }
  }

  const headerNode = (
    <Card.Header>
      <div className="flex items-center gap-2">
        <Icon source={<LightbulbIcon />} />
        <Title as="h3">Oportunidades de Optimizacion</Title>
      </div>
      <Text>Recomendaciones priorizadas para tu proximo experimento</Text>
    </Card.Header>
  )

  if (opportunities.length === 0) {
    const emptyBody = (
      <Card.Body>
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <Text>Se necesitan mas datos para generar recomendaciones.</Text>
        </div>
      </Card.Body>
    )
    if (bare) {
      return (
        <>
          {headerNode}
          {emptyBody}
        </>
      )
    }
    return (
      <Card>
        {headerNode}
        {emptyBody}
      </Card>
    )
  }

  const body = (
    <Card.Body>
      <div className="space-y-3">
        {opportunities.slice(0, 5).map((op, index) => {
          const priorityInfo = getPriorityLabel(op.priority)
          return (
            <div
              key={index}
              className={`rounded-lg p-4 ${getCardStyle(op.priority)}`}
            >
              <div className="flex items-start gap-3">
                <div className={`mt-0.5 ${
                  op.priority <= 2 ? 'text-red-600' :
                  op.priority <= 4 ? 'text-amber-600' : 'text-blue-600'
                }`}>
                  {op.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Tag appearance={priorityInfo.appearance}>
                      {priorityInfo.label}
                    </Tag>
                    <Tag appearance="neutral">
                      {getTypeLabel(op.type)}
                    </Tag>
                    {op.metric && (
                      <Text fontSize="caption" as="span">
                        {op.metric}
                      </Text>
                    )}
                  </div>
                  <Title as="h4">
                    {op.title}
                  </Title>
                  <Text fontSize="caption">
                    {op.description}
                  </Text>
                  {op.action && (
                    <div className="flex items-center gap-1 mt-2 text-xs font-medium text-primary">
                      <Icon source={<ArrowRightIcon />} color="currentColor" />
                      <span>{op.action}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })}

        {opportunities.length > 5 && (
          <Text fontSize="caption" className="text-center block pt-2">
            +{opportunities.length - 5} oportunidades adicionales disponibles
          </Text>
        )}
      </div>
    </Card.Body>
  )

  if (bare) {
    return (
      <>
        {headerNode}
        {body}
      </>
    )
  }
  return (
    <Card>
      {headerNode}
      {body}
    </Card>
  )
}
